import { MutationCache } from "@tanstack/react-query";
import { toast } from "sonner";
import type i18n from "@/i18n/index.ts";
import type { ReplayFailure } from "@/lib/mutationRegistry.ts";
import {
	isOfflineEnqueueError,
	isOfflineQueued,
} from "@/lib/offline/defineOfflineMutation.ts";
import { getSnapshot as reachabilitySnapshot } from "@/lib/offline/reachability.ts";
import { extractApiError } from "@/utils/apiErrorMapper.ts";
import { resolveReplayErrorMessage } from "@/utils/apiErrorMessage.ts";

type I18n = typeof i18n;

/**
 * Cross-cutting toast + offline-enqueue policy that runs for every mutation.
 * Extracted from main.tsx so the composition root stays composed and the
 * policy is unit-testable in isolation.
 *
 * Responsibilities in order:
 *   1. R-2 honest disclosure — warn when a non-offline-capable mutation fires
 *      while unreachable (the change will be lost on close).
 *   2. Loading/success toast lifecycle keyed by `mutation.meta.toastMessages`.
 *   3. Offline disclosure: an offline-capable write resolves with the
 *      `OFFLINE_QUEUED` sentinel (the enqueue itself lives in
 *      `defineOfflineMutation`, so `onSuccess`/`isPending` stay honest) — we
 *      swap the success toast for the "saved offline" toast when we see it,
 *      and surface an `OfflineEnqueueError` as its own failure toast.
 *   4. i18n-aware error toast, using `errors.api.<code>` when the backend
 *      returns a recognized error code.
 */
export function createMutationCache(i18nInstance: I18n): MutationCache {
	const mutationToastMap = new Map<number, string | number>();

	return new MutationCache({
		onMutate: (_variables, mutation) => {
			if (mutation.meta?.silent) return;

			if (!reachabilitySnapshot() && !mutation.meta?.offlineQueueKey) {
				toast.warning(i18nInstance.t("offline.mutations.willBeLost"), {
					duration: 6000,
				});
			}

			const meta = mutation.meta;
			if (meta?.toastMessages?.loading) {
				const toastId = toast.loading(meta.toastMessages.loading);
				mutationToastMap.set(mutation.mutationId, toastId);
			}
		},
		onSuccess: (data, _variables, _context, mutation) => {
			const toastId = mutationToastMap.get(mutation.mutationId);
			mutationToastMap.delete(mutation.mutationId);

			// Offline-capable write queued instead of sent. Reported even for
			// `silent` mutations: queueing is a state change the user must see —
			// unless the caller discloses it itself on its own surface
			// (`offlineQueuedToast: false`), which is never true for a domain record.
			if (isOfflineQueued(data)) {
				if (mutation.meta?.offlineQueuedToast === false) {
					if (toastId) toast.dismiss(toastId);
					return;
				}
				toast.info(i18nInstance.t("offline.mutations.queued"), { id: toastId });
				return;
			}

			if (mutation.meta?.silent) return;
			const meta = mutation.meta;
			if (meta?.toastMessages?.success) {
				toast.success(meta.toastMessages.success, { id: toastId });
			} else if (toastId) {
				toast.dismiss(toastId);
			}
		},
		onError: (error, _variables, _context, mutation) => {
			const meta = mutation.meta;
			const toastId = mutationToastMap.get(mutation.mutationId);
			mutationToastMap.delete(mutation.mutationId);

			// The write couldn't even be persisted to the offline queue — a real
			// failure the user must know about (shown regardless of `silent`).
			if (isOfflineEnqueueError(error)) {
				toast.error(i18nInstance.t("offline.mutations.enqueueFailed"), {
					id: toastId,
				});
				return;
			}

			if (mutation.meta?.silent) return;

			// The caller renders this failure itself — an inline alert that names
			// which refusal it was and what to do about it. A generic toast beside it
			// is at best a duplicate and at worst a contradiction ("please try again"
			// over a refusal that can never succeed on retry).
			if (mutation.meta?.errorsHandledByCaller) {
				if (toastId) toast.dismiss(toastId);
				return;
			}

			// Normalize the live error into a ReplayFailure so the same total
			// resolver serves both write paths. `meta.toastMessages.error` enters
			// only here, as the online caller's per-mutation fallback (D1).
			const apiError = extractApiError(error);
			const outcome: ReplayFailure = apiError
				? { type: "server-error", apiError }
				: { type: "network-error" };
			const message = resolveReplayErrorMessage(
				outcome,
				i18nInstance.t.bind(i18nInstance),
				i18nInstance,
				meta?.toastMessages?.error,
			);
			toast.error(message, { id: toastId });
		},
	});
}
